/**
 * @fileoverview One-time display modal for a freshly issued MCP token.
 * @module features/mcp/components/IssuedTokenDialog
 */

import { useMemo, useState, type ReactElement } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import type { IssuedMcpToken } from '../schemas/mcpToken'
import { buildClaudeDesktopConfig } from '../utils/buildClientConfig'
import { computeMcpUrl } from '../utils/computeMcpUrl'

interface IssuedTokenDialogProps {
  token: IssuedMcpToken | null
  onClose: () => void
}

/**
 * Surfaces the plaintext token exactly once with an explicit one-time-only warning,
 * a copy-to-clipboard control, and a pre-filled Claude Desktop config snippet so
 * the user can paste it straight into `claude_desktop_config.json` without
 * stitching together the URL + token themselves.
 */
export function IssuedTokenDialog({ token, onClose }: IssuedTokenDialogProps): ReactElement | null {
  const [tokenCopied, setTokenCopied] = useState(false)
  const [configCopied, setConfigCopied] = useState(false)

  const configSnippet = useMemo(
    () => (token ? buildClaudeDesktopConfig(computeMcpUrl(), token.plaintextToken) : ''),
    [token],
  )

  if (token == null) return null

  const handleCopyToken = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(token.plaintextToken)
      setTokenCopied(true)
      toast.success('Token copied to clipboard')
    } catch {
      toast.error('Could not copy token. Select and copy manually.')
    }
  }

  const handleCopyConfig = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(configSnippet)
      setConfigCopied(true)
      toast.success('Config copied to clipboard')
    } catch {
      toast.error('Could not copy config. Select and copy manually.')
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Your new MCP token</DialogTitle>
          <DialogDescription>
            Copy the token and the Claude Desktop config snippet now — this is the only time the
            token is shown.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-700 dark:text-amber-300">
            <strong>Copy this token now.</strong> For your security it will not be shown
            again. If you lose it, revoke it and generate a new one.
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Token</p>
            <code
              data-testid="issued-mcp-token-value"
              className="block break-all rounded-md bg-muted p-3 font-mono text-xs"
            >
              {token.plaintextToken}
            </code>
            <Button size="sm" variant="secondary" onClick={() => void handleCopyToken()}>
              {tokenCopied ? 'Copied' : 'Copy token'}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">MCP client config (ready to paste)</p>
            <pre
              data-testid="issued-mcp-config-snippet"
              className="max-h-64 overflow-auto rounded-md bg-muted p-3 font-mono text-xs"
            >
              {configSnippet}
            </pre>
            <Button size="sm" variant="secondary" onClick={() => void handleCopyConfig()}>
              {configCopied ? 'Copied' : 'Copy config'}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
